import React, { useState, useEffect } from 'react';
import { Symptom, MAIN_SYMPTOMS } from '../lib/types';
import { formatIsoDate } from '../lib/dateUtils';
import { triggerHaptic } from '../lib/haptics';
import { convert5ScaleTo10Scale, normalizeSymptomScores } from '../lib/storage';
import {
  X,
  Activity,
  Calendar,
  Clock,
  Trash2,
  SlidersHorizontal,
} from 'lucide-react';
import { getActiveTrackableSymptoms, TrackableSymptom } from '../lib/symptomCategories';
import { CustomizeSymptomsModal } from './CustomizeSymptomsModal';
import { Sheet } from './Sheet';

interface SymptomLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSymptom: (symptom: Omit<Symptom, 'id'>) => void;
  editingSymptom?: Symptom | null;
  onUpdateSymptom?: (symptom: Symptom) => void;
  onDeleteSymptom?: (id: string) => void;
}

const DEFAULT_SCORES: Record<string, number> = {
  bloating: 0,
  stomach_pain: 0,
  heartburn: 0,
  reflux: 0,
  nausea: 0,
  vomiting: 0,
  diarrhea: 0,
  constipation: 0,
  headache: 0,
  mouth_taste: 0,
  eczema: 0,
  hayfever: 0,
  asthma: 0,
};

export const SymptomLogModal: React.FC<SymptomLogModalProps> = ({
  isOpen,
  onClose,
  onSaveSymptom,
  editingSymptom,
  onUpdateSymptom,
  onDeleteSymptom,
}) => {
  const [scores, setScores] = useState<Record<string, number>>(DEFAULT_SCORES);
  const [overallWellbeing, setOverallWellbeing] = useState<number>(4);
  const [notes, setNotes] = useState<string>('');
  const [activeSymptoms, setActiveSymptoms] = useState<TrackableSymptom[]>(() => getActiveTrackableSymptoms());
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const handleUpdate = () => {
      setActiveSymptoms(getActiveTrackableSymptoms());
    };
    window.addEventListener('digestlog_symptoms_updated', handleUpdate);
    return () => window.removeEventListener('digestlog_symptoms_updated', handleUpdate);
  }, []);

  const [entryDate, setEntryDate] = useState(() => formatIsoDate(new Date()));
  const [entryTime, setEntryTime] = useState(() => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  });

  useEffect(() => {
    if (isOpen) {
      if (editingSymptom) {
        const norm = normalizeSymptomScores(editingSymptom);
        const initialScores = { ...DEFAULT_SCORES, ...norm.symptom_scores };

        setScores(initialScores);
        setOverallWellbeing(norm.overall_wellbeing || 4);
        setNotes(norm.notes || '');

        if (norm.timestamp_iso) {
          const d = new Date(norm.timestamp_iso);
          if (!isNaN(d.getTime())) {
            setEntryDate(formatIsoDate(d));
            const hh = String(d.getHours()).padStart(2, '0');
            const mm = String(d.getMinutes()).padStart(2, '0');
            setEntryTime(`${hh}:${mm}`);
          } else {
            setEntryDate(norm.date || formatIsoDate(new Date()));
          }
        } else if (norm.date) {
          setEntryDate(norm.date);
        }
      } else {
        const now = new Date();
        setScores(DEFAULT_SCORES);
        setOverallWellbeing(4);
        setNotes('');
        setEntryDate(formatIsoDate(now));
        const hh = String(now.getHours()).padStart(2, '0');
        const mm = String(now.getMinutes()).padStart(2, '0');
        setEntryTime(`${hh}:${mm}`);
      }
    }
  }, [editingSymptom, isOpen]);

  const handleScoreChange = (symptomId: string, val: number) => {
    triggerHaptic('selection');
    setScores((prev) => ({
      ...prev,
      [symptomId]: val,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const timestampIso = `${entryDate}T${entryTime}:00`;

    // Active non-zero symptoms for tags compatibility
    const activeTags = activeSymptoms
      .filter((s) => (scores[s.id] || 0) > 0)
      .map((s) => `${s.name} (${scores[s.id]}/10)`);

    if (editingSymptom && onUpdateSymptom) {
      onUpdateSymptom({
        ...editingSymptom,
        date: entryDate,
        timestamp_iso: timestampIso,
        bloating_score: scores.bloating || 0,
        soreness_score: scores.stomach_pain || 0,
        overall_wellbeing: overallWellbeing,
        symptom_scores: scores,
        bad_taste_score: scores.mouth_taste || undefined,
        symptom_tags: activeTags.join(', '),
        notes: notes.trim() || undefined,
      });
    } else {
      onSaveSymptom({
        date: entryDate,
        timestamp_iso: timestampIso,
        checkin_type: 'General Daily',
        associated_meal_id: null,
        bloating_score: scores.bloating || 0,
        soreness_score: scores.stomach_pain || 0,
        overall_wellbeing: overallWellbeing,
        symptom_scores: scores,
        bad_taste_score: scores.mouth_taste || undefined,
        symptom_tags: activeTags.join(', '),
        notes: notes.trim() || undefined,
      });
    }
    onClose();
  };

  return (
    <Sheet isOpen={isOpen} onClose={onClose} ariaLabel={editingSymptom ? 'Edit check-in' : 'Log check-in'}>
      <div className="px-5 pt-1 pb-4 flex flex-col flex-1 min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-zinc-800/80 shrink-0">
          <div className="flex items-center space-x-2">
            <div className="p-2 rounded-xl bg-slate-100 dark:bg-zinc-800 text-slate-900 dark:text-white">
              <Activity className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-sm font-black text-slate-950 dark:text-white">
                {editingSymptom ? 'Edit Check-In' : 'Log Check-In'}
              </h2>
              <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-500">
                0 to 10 Severity Scale
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-zinc-800 transition-all cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0 overflow-hidden pt-3">
          {/* Scrollable Body: All Symptoms -> Wellbeing -> Notes -> Date/Time */}
          <div className="flex-1 overflow-y-auto overscroll-contain pr-1 space-y-4">
            {/* Thinner flat symptom list matching reference */}
            <div className="space-y-1">
              <div className="flex items-center justify-between pb-1.5 px-1 border-b border-slate-100 dark:border-zinc-800/80">
                <span className="text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                  Symptoms
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsCustomizeOpen(true)}
                    className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-blue-50 dark:bg-blue-950/50 hover:bg-blue-100 dark:hover:bg-blue-900/60 text-[10px] font-bold text-blue-600 dark:text-blue-400 transition-colors cursor-pointer"
                    title="Customize symptom sliders"
                  >
                    <SlidersHorizontal className="w-3 h-3" />
                    <span>Customize</span>
                  </button>
                  <span className="text-[10px] font-semibold text-slate-400 dark:text-zinc-500">
                    0 to 10 Scale
                  </span>
                </div>
              </div>

              <div className="divide-y divide-slate-100 dark:divide-zinc-800/60">
                {activeSymptoms.map((sym) => {
                  const currentScore = scores[sym.id] || 0;
                  const isMouthTaste = sym.id === 'mouth_taste';
                  return (
                    <div
                      key={sym.id}
                      className="py-2.5 flex items-center justify-between gap-2.5 transition-colors"
                    >
                      {/* Name on the left */}
                      <div className="w-24 sm:w-28 shrink-0">
                        <span className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white block leading-tight truncate" title={sym.name}>
                          {sym.name}
                        </span>
                        {sym.isCustom && (
                          <span className="text-[8px] font-bold px-1 py-0.2 rounded bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300">
                            CUSTOM
                          </span>
                        )}
                      </div>

                      {/* Scale slider to the right of the name */}
                      <div className="flex-1 flex flex-col justify-center px-1">
                        <input
                          type="range"
                          min="0"
                          max="10"
                          step="1"
                          value={currentScore}
                          onChange={(e) => handleScoreChange(sym.id, parseInt(e.target.value, 10))}
                          className="w-full h-1.5 bg-slate-200 dark:bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-blue-600 dark:accent-blue-500"
                        />
                        <div className="flex justify-between text-[8px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 px-0.5 mt-0.5 select-none">
                          <span>{isMouthTaste ? 'Pleasant' : 'Low'}</span>
                          <span>{isMouthTaste ? 'Unpleasant' : 'High'}</span>
                        </div>
                      </div>

                      {/* Score to the right of the slider */}
                      <div className="w-6 text-right shrink-0">
                        <span
                          className={`text-xs sm:text-sm font-black tabular-nums ${
                            currentScore > 0
                              ? currentScore >= 7
                                ? 'text-rose-600 dark:text-rose-400'
                                : currentScore >= 4
                                ? 'text-amber-600 dark:text-amber-400'
                                : 'text-blue-600 dark:text-blue-400'
                              : 'text-slate-400 dark:text-zinc-500'
                          }`}
                        >
                          {currentScore}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Overall Wellbeing (1–5) */}
            <div className="space-y-1.5 pt-2 border-t border-slate-100 dark:border-zinc-800/80">
              <div className="flex items-center justify-between px-1">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                  Overall Vitality & Wellbeing
                </label>
                <span className="text-xs font-bold text-slate-700 dark:text-zinc-300">
                  {overallWellbeing} / 5
                </span>
              </div>
              <div className="grid grid-cols-5 gap-1.5">
                {[1, 2, 3, 4, 5].map((score) => (
                  <button
                    key={score}
                    type="button"
                    onClick={() => {
                      triggerHaptic('selection');
                      setOverallWellbeing(score);
                    }}
                    className={`py-2 rounded-xl text-xs font-bold transition-all ${
                      overallWellbeing === score
                        ? 'bg-slate-950 dark:bg-white text-white dark:text-black font-black shadow-xs'
                        : 'bg-slate-100 dark:bg-zinc-800/80 text-slate-600 dark:text-zinc-400 border border-slate-200 dark:border-zinc-800'
                    }`}
                  >
                    {score}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1 pt-2 border-t border-slate-100 dark:border-zinc-800/80">
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 block px-1">
                Notes (Sensations, Context, Triggers)
              </label>
              <textarea
                rows={2}
                placeholder="Add additional notes or specific observations..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 dark:bg-zinc-900/60 border border-slate-200 dark:border-zinc-800 rounded-xl text-xs font-bold text-slate-950 dark:text-white focus:outline-none resize-none"
              />
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 dark:border-zinc-800/80">
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 block mb-1 px-1">
                  Date
                </label>
                <input
                  type="date"
                  value={entryDate}
                  onChange={(e) => setEntryDate(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-zinc-900/60 border border-slate-200 dark:border-zinc-800 rounded-xl text-xs font-bold text-slate-950 dark:text-white focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 block mb-1 px-1">
                  Time
                </label>
                <input
                  type="time"
                  value={entryTime}
                  onChange={(e) => setEntryTime(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-zinc-900/60 border border-slate-200 dark:border-zinc-800 rounded-xl text-xs font-bold text-slate-950 dark:text-white focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons (Sticky Footer) */}
          <div className="flex items-center space-x-2 pt-3 mt-2 border-t border-slate-100 dark:border-zinc-800 shrink-0">
            {editingSymptom && onDeleteSymptom && (
              !showDeleteConfirm ? (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-2.5 rounded-2xl bg-rose-50 dark:bg-rose-950/40 text-rose-600 hover:bg-rose-100 dark:hover:bg-rose-900/60 transition-all cursor-pointer"
                  title="Delete Entry"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              ) : (
                <div className="flex items-center space-x-1.5 animate-in fade-in">
                  <button
                    type="button"
                    onClick={() => {
                      onDeleteSymptom(editingSymptom.id);
                      onClose();
                    }}
                    className="px-3 py-2 rounded-xl bg-rose-600 text-white font-bold text-[11px] shadow-sm active:scale-95 transition-all cursor-pointer"
                  >
                    Confirm Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-2 py-2 rounded-xl bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400 font-bold text-[11px] hover:bg-slate-200 transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              )
            )}

            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-2xl border border-slate-200 dark:border-zinc-800 text-xs font-bold text-slate-600 dark:text-zinc-400 hover:bg-slate-50 dark:hover:bg-zinc-800 transition-all cursor-pointer"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="flex-1 py-2.5 rounded-2xl bg-slate-950 dark:bg-white text-white dark:text-black text-xs font-black shadow-xs hover:opacity-90 active:scale-95 transition-all cursor-pointer"
            >
              {editingSymptom ? 'Save Changes' : 'Save Check-In'}
            </button>
          </div>
        </form>
      </div>

      {/* Customize Sliders Modal */}
      <CustomizeSymptomsModal
        isOpen={isCustomizeOpen}
        onClose={() => setIsCustomizeOpen(false)}
      />
    </Sheet>
  );
};
